import { datadogRum } from "@datadog/browser-rum";
import { keepPreviousData } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Dialog, Select, SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportTemplateReportTypeChoices,
  ReportTypeEnums,
  useCreateReportMutation,
  useGetProviderLearnersQuery,
  useGetReportsQuery,
} from "@api/graphql/types-and-hooks";
import { Evaluation } from "@components/EvaluationsReportsTable/EvaluationsReportsTable";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useReportTemplateSelection } from "@hooks/useReportTemplateSelection";
import { useAdminData } from "@providers/AdminDataProvider";

interface NewEvaluationDialogProps {
  setDialogOpen: (open: boolean) => void;
  isDialogOpen: boolean;
}

export const NewEvaluationDialog: React.FC<NewEvaluationDialogProps> = ({ isDialogOpen, setDialogOpen }) => {
  const [learnerName, setLearnerName] = useState<string>("");
  const [selected, setSelected] = useState<SelectItem | undefined>();
  const go = useNavigate();
  const reportType = ReportTypeEnums.DiagnosisEvaluation;
  const { isPassageHealthUser } = useAdminData();
  const { templateSelections, templatesLoading, selectedTemplate, setSelectedTemplate } = useReportTemplateSelection(
    ApiReportTemplateReportTypeChoices.DiagnosisEvaluation,
    "get-report-templates-diagnosis-evaluation",
    selected?.id,
  );

  useEffect(() => {
    setSelected(undefined);
    setLearnerName("");
  }, [isDialogOpen]);

  // Get all of the client names to populate in the drop down
  const learnerQuery = useGetProviderLearnersQuery(
    { learnerName },
    {
      queryKey: ["get-provider-learners", learnerName],
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      placeholderData: keepPreviousData,
    },
  );

  const getLearnerEvaluation = useGetReportsQuery(
    {
      filter: {
        learnerId: selected?.id,
        reportType,
      },
    },
    {
      queryKey: ["get-learner-evaluation", selected?.id],
      enabled: false,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const createLearnerEvaluationMutation = useCreateReportMutation({});

  // Data for all current clients(learners)
  const { data, isLoading } = learnerQuery;
  // Data in case a client(learner) already has an evaluation
  const { data: learnerEvaluationData, isLoading: learnerEvaluationLoading } = getLearnerEvaluation;
  // Data to create a new evaluation
  const { data: createEvaluationData, isPending, error } = createLearnerEvaluationMutation;

  const itemsData = useMemo(() => {
    const items: SelectItem[] = [];

    if (data?.getProviderLearners) {
      const learnerItems = data.getProviderLearners?.edges
        .map((learner) => ({
          primary: `${learner?.node?.learnermetadata?.firstName} ${learner?.node?.learnermetadata?.lastName}`,
          id: learner?.node?.id,
          className: "ph-no-capture",
        }))
        .sort((la, lb) => (la.primary > lb.primary ? 1 : -1));
      return learnerItems;
    }

    return items;
  }, [data]);

  const createReportObject = async () => {
    const response = await createLearnerEvaluationMutation.mutateAsync({
      reportType: reportType,
      learnerId: selected?.id,
      reportTemplateId: selectedTemplate?.id,
    });

    // log creation to datadog
    const base64DecodedId = atob(response.createReport?.report?.id || "");
    const databaseId = base64DecodedId.split("ReportType:")?.pop();
    datadogRum.addAction("report-creation", {
      reportId: databaseId,
      reportType: reportType,
    });
  };

  useEffect(() => {
    if (error) {
      console.error("Error while creating evaluation", error);
      notifyError("Error creating evaluation");
    }
  }, [error]);

  useEffect(() => {
    if (!createEvaluationData) return;

    const evaluation = createEvaluationData.createReport;
    const reportStatus = evaluation?.status;

    if (reportStatus) {
      notifySuccess(evaluation?.message || "Evaluation created successfully");

      const learnerEvaluation = evaluation?.report;

      if (learnerEvaluation) {
        const { clientFirstName, clientLastName } = learnerEvaluation?.reportClientDetails || {};

        setDialogOpen(false);
        go(`/evaluations/${learnerEvaluation?.id}`, {
          state: {
            id: learnerEvaluation?.id,
            clientName: `${clientFirstName || "N/A"} ${clientLastName || ""}`,
            dateCreated: new Date(learnerEvaluation?.createdDate) || new Date(),
            lastUpdated: new Date(learnerEvaluation?.lastUpdatedDate) || new Date(),
          },
        });
      }
    } else {
      notifyError(evaluation?.message || "Error creating evaluation");
    }
  }, [createEvaluationData]);

  const handleSecondaryButtonClick = () => {
    setDialogOpen(false);
  };

  useEffect(() => {
    const validateObject = async () => {
      await getLearnerEvaluation.refetch();
    };

    if (selected) {
      validateObject();
    }
  }, [selected]);

  const goToReport = () => {
    setDialogOpen(false);
    go(`/evaluations/${learnerEvaluationData?.getReports?.edges[0]?.node?.id}`, {
      state: {
        id: learnerEvaluationData?.getReports?.edges[0]?.node?.id,
        clientName: selected?.primary,
        dateCreated: new Date(learnerEvaluationData?.getReports?.edges[0]?.node?.createdDate),
        lastUpdated: new Date(learnerEvaluationData?.getReports?.edges[0]?.node?.lastUpdatedDate),
      } as Evaluation,
    });
  };

  const primaryButtonDisableStatus = () => {
    const evaluationExistsForClient = !!(selected && learnerEvaluationData?.getReports?.edges[0]?.node);
    const templateRequired = !selectedTemplate;

    return evaluationExistsForClient || isPending || templateRequired;
  };

  return (
    <Dialog
      title="New Evaluation"
      open={isDialogOpen}
      setOpen={setDialogOpen}
      primaryButton={{
        text: isPending ? "Creating..." : "Yes, Continue",
        onClick: createReportObject,
        disabled: primaryButtonDisableStatus(),
      }}
      secondaryButton={{ text: "No, Cancel", onClick: handleSecondaryButtonClick }}
      size="xl"
    >
      <Select
        title="Select Client"
        isLoading={isLoading}
        items={itemsData}
        placeholderText="Type to find a client"
        selected={selected}
        setSelected={(val) => {
          setSelected(val);
          if (val?.id) {
            setLearnerName("");
          }
        }}
        searchValue={learnerName}
        onSearch={(inputValue: string) => setLearnerName(inputValue)}
        searchDebounceMs={400}
        actionItem={
          isPassageHealthUser
            ? undefined
            : {
                label: "Create New",
                onClick: () => createReportObject(),
              }
        }
        enableSearch
      />
      <Select
        title="Select Template"
        isLoading={templatesLoading}
        items={templateSelections}
        placeholderText={
          templateSelections.length > 1 && !selectedTemplate ? "Select a template..." : "Type to find a template"
        }
        selected={selectedTemplate}
        setSelected={setSelectedTemplate}
        enableSearch
      />
      {learnerEvaluationLoading && <Small>Loading...</Small>}
      {learnerEvaluationData?.getReports?.edges[0]?.node ? (
        <>
          <Small displayType="normal" className="-mt-6">
            An evaluation for this client already exists.
            <br /> Click{" "}
            <span className="cursor-pointer text-secondary underline" onClick={() => goToReport()}>
              here
            </span>{" "}
            to view
          </Small>
        </>
      ) : (
        <></>
      )}
    </Dialog>
  );
};
